import { observer, Show, use$ } from "@legendapp/state/react"
import { Button, HoverCard, HoverCardDropdown, HoverCardTarget, Text } from "@mantine/core"
import { IconCrown } from "@tabler/icons-react"
import { useModel } from "@/hooks/useModel"
import { useTranslations } from "next-intl"
import { CTAButton } from "@/components/generic/CTAButton/CTAButton"
import { syncStore$ } from "@/stores/sync.v2.store"
import { buildSubscribeHref } from "@/app/subscribe/utils"
import type { TLoginUserData } from "@/schemas/user.schema"
import { getAccountTrialStatus } from "@/lib/accountTrialState"
import { useEffect, useState } from "react"

export const HeaderButtonSubscribe = observer(function HeaderButtonSubscribe({ userData }: { userData?: TLoginUserData }) {
  const { Account } = useModel()
  const t = useTranslations("TrialBanner")
  const [showAnimatedButton, setShowAnimatedButton] = useState(false)

  // const isTrialBannerHidden = use$(appStore$.isTrialBannerHidden)
  const isAdmin = use$(() => {
    const admin = syncStore$.userStatus.isAdmin.get()
    const isOwner = syncStore$.userStatus.isOwner.get()
    return admin || isOwner
  })
  const accountHash = use$(syncStore$.userStatus.rg_acct_hash)
  const resolvedTrialStatus = userData ? getAccountTrialStatus(userData.account) : Account.trialStatus
  const resolvedIsSoloFree = userData?.account.isSoloFree ?? Account.isSoloFree
  const resolvedTrialDaysRemaining = userData?.account.trialDaysRemaining ?? Account.trialDaysRemaining
  const resolvedTrialMinutesRemaining = userData?.account.trialMinutesRemaining ?? Account.trialMinutesRemaining
  const resolvedSoloTrialMinutesRemaining = userData?.account.soloTrialMinutesRemaining ?? Account.soloTrialMinutesRemaining
  const resolvedSoloTrialCurrentPeriodResetDate = userData?.account.soloTrialCurrentPeriodResetDate ?? Account.soloTrialCurrentPeriodResetDate
  const resolvedIsAdmin = userData ? Boolean(userData.user.isAdmin || userData.user.isOwner) : isAdmin
  const resolvedAccountHash = typeof userData?.user.rg_acct_hash === "string" ? userData.user.rg_acct_hash : typeof accountHash === "string" ? accountHash : null
  const subscribeHref = buildSubscribeHref(resolvedAccountHash)

  useEffect(() => {
    setShowAnimatedButton(true)
  }, [])

  if (resolvedTrialStatus === "notInTrial") return null

  // if (isTrialBannerHidden === false) return null

  // Dont show the trial banner if the user is not an admin
  if (!resolvedIsAdmin) return null

  return (
    <HoverCard width={280} shadow="md">
      <HoverCardTarget>
        {showAnimatedButton ? (
          <CTAButton href={subscribeHref} duration={20000} leftSection={<IconCrown size={16} />} animationColor="cyan.3">
            {t("upgrade")}
          </CTAButton>
        ) : (
          <Button component="a" href={subscribeHref} variant="filled" size="sm" radius="sm" leftSection={<IconCrown size={16} />}>
            {t("upgrade")}
          </Button>
        )}
      </HoverCardTarget>
      <HoverCardDropdown>
        <Show if={resolvedTrialStatus === "trialExpired" && resolvedIsSoloFree === false}>{() => <Text size="sm">{t("hoverCard.expired")}</Text>}</Show>

        <Show if={resolvedTrialStatus === "trialExpired" && resolvedIsSoloFree === true}>{() => <Text size="sm">{t("hoverCard.soloExpired", { resetDate: resolvedSoloTrialCurrentPeriodResetDate ?? "" })}</Text>}</Show>

        <Show if={resolvedTrialStatus === "inTrial" && resolvedIsSoloFree === false}>
          {() => (
            <Text size="sm">
              {t("hoverCard.daysAndMinutesLeft", {
                days: resolvedTrialDaysRemaining ?? 0,
                minutes: resolvedTrialMinutesRemaining ?? 0,
              })}
            </Text>
          )}
        </Show>

        <Show if={resolvedTrialStatus === "inTrial" && resolvedIsSoloFree === true}>{() => <Text size="sm">{t("hoverCard.soloMinutesLeft", { minutes: resolvedSoloTrialMinutesRemaining ?? 0 })}</Text>}</Show>
      </HoverCardDropdown>
    </HoverCard>
  )
})
